
import React, { useState, useEffect, useMemo } from 'react';
import { Transaction, CreditCard, CategoryType } from '../types';
import { formatCurrency } from '../utils/helpers';
import { createYearlyCardReminders } from '../utils/googleCalendar';
import TransactionModal from './TransactionModal';
import ConfirmDialog from './ConfirmDialog';
import CardModal from './CardModal';
import CardStatementModal from './CardStatementModal';

interface CardsProps {
  cards: CreditCard[];
  onAddCard: (card: CreditCard) => void;
  onUpdateCard: (card: CreditCard) => void;
  onDeleteCard: (id: string) => void;
  onTriggerVerify: (title: string, message: string, callback: () => void, icon?: string) => void;
  transactions: Transaction[];
  onAddTransaction: (t: Transaction | Transaction[]) => void;
  needsSetup: boolean;
  onTriggerSetup: () => void;
}

const CARD_GRADIENTS = [
  "from-slate-800 via-slate-900 to-slate-950",
  "from-blue-700 via-indigo-900 to-slate-950",
  "from-emerald-700 via-teal-900 to-slate-950",
  "from-violet-700 via-purple-900 to-slate-950",
  "from-rose-700 via-red-900 to-slate-950",
  "from-cyan-700 via-blue-900 to-slate-950",
];

const Cards: React.FC<CardsProps> = ({ 
  cards, onAddCard, onUpdateCard, onDeleteCard, onTriggerVerify, transactions, onAddTransaction, needsSetup, onTriggerSetup
}) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [editingCard, setEditingCard] = useState<CreditCard | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ isOpen: boolean; id: string | null; type: 'confirm' | 'alert' }>({ isOpen: false, id: null, type: 'confirm' });
  const [payBillData, setPayBillData] = useState<Transaction | undefined>(undefined);
  const [confirmReminders, setConfirmReminders] = useState<{ isOpen: boolean; card: CreditCard | null }>({ isOpen: false, card: null });
  const [statementData, setStatementData] = useState<{ isOpen: boolean; card: CreditCard | null; transactions: Transaction[]; amount: number }>({ 
    isOpen: false, card: null, transactions: [], amount: 0 
  });
  
  const [isProcessingReminders, setIsProcessingReminders] = useState(false);
  const [reminderFeedback, setReminderFeedback] = useState<{show: boolean, msg: string, type: 'info'|'success'|'error'}>({show: false, msg: '', type: 'info'});

  const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  const availableYears = useMemo(() => {
    const years = [2024, 2025, 2026, 2027];
    transactions.forEach(t => {
      const y = parseInt(t.date.split('-')[0]);
      if (y && !years.includes(y)) years.push(y);
    });
    return [...new Set(years)].sort();
  }, [transactions]);

  const [showValues, setShowValues] = useState(() => {
    const saved = localStorage.getItem('equilibra_privacy_mode');
    return saved === null ? true : saved === 'true';
  });

  useEffect(() => {
    const handleSync = () => {
      const saved = localStorage.getItem('equilibra_privacy_mode');
      setShowValues(saved === null ? true : saved === 'true');
    };
    window.addEventListener('privacyModeChanged', handleSync);
    return () => window.removeEventListener('privacyModeChanged', handleSync);
  }, []);

  const togglePrivacy = () => {
    const newVal = !showValues;
    setShowValues(newVal);
    localStorage.setItem('equilibra_privacy_mode', newVal.toString());
    window.dispatchEvent(new Event('privacyModeChanged'));
  };

  const handleAddClick = () => {
    if (needsSetup) {
      onTriggerSetup();
    } else {
      setEditingCard(null);
      setIsCardModalOpen(true);
    }
  };

  const maskValue = (val: number) => {
    if (!showValues) return "R$ ••••";
    return formatCurrency(val);
  };

  const handleCreateYearlyReminders = async (card: CreditCard) => {
    setIsProcessingReminders(true);
    setReminderFeedback({ show: true, msg: `Agendando 12 lembretes para o cartão ${card.name}...`, type: 'info' });
    // Usamos o dia de vencimento para os lembretes
    const success = await createYearlyCardReminders(card.name, card.dueDate || card.closingDate);
    if (success) setReminderFeedback({ show: true, msg: "Lembretes anuais criados com sucesso!", type: 'success' });
    else setReminderFeedback({ show: true, msg: "Erro ao criar agenda.", type: 'error' });
    setIsProcessingReminders(false);
    setTimeout(() => setReminderFeedback(prev => ({ ...prev, show: false })), 5000);
  };

  const handlePayBill = (card: CreditCard, amount: number) => {
    setPayBillData({
      id: '', description: `Pagamento Fatura: ${card.name}`,
      amount: Math.max(0, amount), type: 'expense', category: 'Pagamento de Fatura' as CategoryType,
      date: new Date().toISOString().split('T')[0], cardId: card.id
    });
    setIsModalOpen(true);
  };

  const handleEditRequest = (card: CreditCard) => {
    onTriggerVerify(
      'Autorizar Alteração de Cartão',
      `Para modificar os dados do cartão ${card.name}, insira sua Chave Mestra.`,
      () => {
        setEditingCard(card);
        setIsCardModalOpen(true);
      },
      'fa-credit-card'
    );
  };

  const handleViewStatement = (card: CreditCard, billTransactions: Transaction[], amount: number) => {
    setStatementData({
      isOpen: true,
      card,
      transactions: billTransactions,
      amount
    });
  };

  const handleDeleteRequest = (cardId: string) => {
    const cardTransactions = transactions.filter(t => t.cardId === cardId);
    const totalDebt = cardTransactions.reduce((acc, t) => {
      if (t.category === 'Pagamento de Fatura') return acc - t.amount;
      return t.type === 'expense' ? acc + t.amount : acc - t.amount;
    }, 0);

    if (totalDebt > 0.01) {
      setConfirmDelete({ isOpen: true, id: cardId, type: 'alert' });
    } else {
      onDeleteCard(cardId);
    }
  };

  return (
    <div className="pb-24 max-w-5xl mx-auto px-1">
      <div className="sticky top-0 z-40 bg-slate-100/80 dark:bg-slate-950/80 backdrop-blur-xl pt-[max(1rem,env(safe-area-inset-top))] pb-6 space-y-4 -mx-4 px-4 border-b border-slate-200/50 dark:border-slate-800/50 md:relative md:bg-transparent md:border-none md:p-0 md:mb-8 transition-all">
        <header className="flex justify-between items-center">
          <div>
            <h1 className="text-xl md:text-3xl font-black text-slate-900 dark:text-slate-50 heading-display uppercase tracking-tight">Meus Cartões</h1>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.5)]"></div>
              <p className="text-slate-500 dark:text-slate-400 text-[9px] md:text-xs uppercase font-black tracking-[0.15em]">Gestão Consolidada • Soberania de Dados</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={togglePrivacy}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-400 dark:text-slate-500 w-9 h-9 md:w-12 md:h-12 rounded-xl flex items-center justify-center shadow-premium active:scale-95 transition-all hover:border-blue-500/50 group"
              title={showValues ? "Ocultar Valores" : "Mostrar Valores"}
            >
              <i className={`fa-solid ${showValues ? 'fa-eye' : 'fa-eye-slash'} text-xs md:text-lg transition-transform group-hover:scale-110`}></i>
            </button>
            <button onClick={handleAddClick} className="bg-blue-600 hover:bg-blue-700 text-white w-9 h-9 md:w-12 md:h-12 rounded-xl flex items-center justify-center shadow-xl shadow-blue-500/20 active:scale-95 transition-all group">
              <i className="fa-solid fa-plus text-sm md:text-xl transition-transform group-hover:rotate-90"></i>
            </button>
          </div>
        </header>

        {reminderFeedback.show && (
          <div className={`p-4 rounded-2xl flex items-center gap-3 animate-in slide-in-from-top duration-500 shadow-2xl ${
            reminderFeedback.type === 'success' ? 'bg-emerald-600 text-white' : 
            reminderFeedback.type === 'error' ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'
          }`}>
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
              {isProcessingReminders ? <i className="fa-solid fa-circle-notch animate-spin"></i> : <i className="fa-solid fa-calendar-check"></i>}
            </div>
            <p className="text-[9px] font-black uppercase tracking-[0.15em] leading-tight">{reminderFeedback.msg}</p>
          </div>
        )}

        <div className="flex gap-2">
          <div className="relative flex-1 md:flex-none md:w-48 group">
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))} className="w-full bg-white dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 rounded-xl text-[9px] font-black uppercase tracking-widest p-3 outline-none px-4 cursor-pointer appearance-none hover:border-blue-500/30 transition-all shadow-sm group-hover:shadow-md">
              {months.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
            <i className="fa-solid fa-chevron-down absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-[9px] group-hover:text-blue-500 transition-colors"></i>
          </div>
          <div className="relative w-24 md:w-28 group">
            <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} className="w-full bg-white dark:bg-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-800 rounded-xl text-[9px] font-black uppercase tracking-widest p-3 outline-none px-4 cursor-pointer appearance-none hover:border-blue-500/30 transition-all shadow-sm group-hover:shadow-md">
              {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <i className="fa-solid fa-calendar absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-[9px] group-hover:text-blue-500 transition-colors"></i>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
        {cards.length === 0 ? (
          <div className="col-span-full py-40 text-center bg-white dark:bg-slate-900 rounded-[4rem] border-2 border-dashed border-slate-200 dark:border-slate-800 animate-in fade-in duration-1000">
            <div className="w-24 h-24 bg-slate-50 dark:bg-slate-800/50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 text-slate-200 dark:text-slate-800 shadow-inner">
              <i className="fa-solid fa-credit-card text-4xl"></i>
            </div>
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Nenhum cartão configurado</p>
          </div>
        ) : (
          cards.map((card, index) => {
            const cardTransactions = transactions.filter(t => t.cardId === card.id);
            
            const billTransactions: Transaction[] = [];
            const billAmount = cardTransactions.reduce((acc, t) => {
              const tDate = new Date(t.date + 'T12:00:00');
              const tDay = tDate.getDate();
              const tMonth = tDate.getMonth();
              const tYear = tDate.getFullYear();
              const closing = card.closingDate || 5;

              let billMonth = tMonth;
              let billYear = tYear;

              if (tDay > closing) {
                billMonth++;
                if (billMonth > 11) {
                  billMonth = 0;
                  billYear++;
                }
              }

              if (billMonth === selectedMonth && billYear === selectedYear) {
                billTransactions.push(t);
                if (t.category === 'Pagamento de Fatura') return acc - t.amount;
                return t.type === 'expense' ? acc + t.amount : acc - t.amount;
              }
              return acc;
            }, 0);

            billTransactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

            const totalOutstandingDebt = cardTransactions.reduce((acc, t) => {
              if (t.category === 'Pagamento de Fatura') return acc - t.amount;
              return t.type === 'expense' ? acc + t.amount : acc - t.amount;
            }, 0);

            const availableLimit = card.limit - totalOutstandingDebt;
            const usagePercent = card.limit > 0 ? (Math.max(0, totalOutstandingDebt) / card.limit) * 100 : 0;

            return (
              <div key={card.id} className="group animate-in fade-in slide-in-from-bottom-10 duration-700" style={{ animationDelay: `${index * 100}ms` }}>
                <div className={`relative aspect-[1.58/1] w-full bg-gradient-to-br ${CARD_GRADIENTS[index % CARD_GRADIENTS.length]} rounded-3xl p-6 md:p-8 shadow-2xl border border-white/20 overflow-hidden flex flex-col justify-between transition-all duration-700 group-hover:-translate-y-2 group-hover:shadow-[0_30px_60px_-10px_rgba(0,0,0,0.3)]`}>
                  <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/graphy.png')] opacity-[0.08] pointer-events-none"></div>
                  <div className="absolute -right-20 -top-20 w-80 h-80 bg-white/10 rounded-full blur-[100px] group-hover:bg-white/20 transition-all duration-700"></div>
                  <div className="absolute -left-20 -bottom-20 w-64 h-64 bg-black/20 rounded-full blur-[80px]"></div>
                  
                  <div className="flex justify-between items-start relative z-10 gap-2">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="w-8 h-5 md:w-12 md:h-8 bg-gradient-to-br from-amber-200 via-amber-400 to-amber-600 rounded-md relative shadow-inner border border-black/10 overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20"></div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-px bg-black/10"></div>
                      </div>
                      <button 
                        onClick={() => setConfirmReminders({ isOpen: true, card })}
                        disabled={isProcessingReminders}
                        className="bg-white/10 hover:bg-white/20 px-2 md:px-3 py-1.5 rounded-lg border border-white/10 flex items-center gap-1.5 transition-all group/btn disabled:opacity-50 backdrop-blur-md active:scale-90"
                      >
                        <i className={`fa-solid ${isProcessingReminders ? 'fa-spinner fa-spin' : 'fa-bell'} text-[8px] text-white/60 group-hover/btn:text-amber-400`}></i>
                        <span className="text-[7px] font-black uppercase text-white/80 tracking-widest">Lembrete</span>
                      </button>
                    </div>
                    
                    <div className="flex flex-col items-end gap-2">
                       <div className="flex gap-1.5">
                          <div className="bg-white/10 backdrop-blur-2xl px-2 py-1.5 rounded-lg border border-white/10 flex items-center gap-1.5 shadow-sm">
                             <i className="fa-solid fa-lock text-[7px] text-amber-400"></i>
                             <span className="text-[7px] font-black text-white/90 uppercase tracking-widest">FECHA {card.closingDate}</span>
                          </div>
                          <div className="bg-white/10 backdrop-blur-2xl px-2 py-1.5 rounded-lg border border-white/10 flex items-center gap-1.5 shadow-sm">
                             <i className="fa-solid fa-calendar-day text-[7px] text-blue-400"></i>
                             <span className="text-[7px] font-black text-white/90 uppercase tracking-widest">VENCE {card.dueDate || card.closingDate}</span>
                          </div>
                       </div>
                       <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-1 group-hover:translate-y-0">
                          <button onClick={() => handleViewStatement(card, billTransactions, billAmount)} className="w-8 h-8 bg-white/10 hover:bg-white/20 text-white/60 hover:text-blue-400 rounded-lg flex items-center justify-center transition-all border border-white/10 backdrop-blur-md active:scale-90" title="Ver Extrato"><i className="fa-solid fa-receipt text-[10px]"></i></button>
                          <button onClick={() => handleEditRequest(card)} className="w-8 h-8 bg-white/10 hover:bg-white/20 text-white/60 hover:text-white rounded-lg flex items-center justify-center transition-all border border-white/10 backdrop-blur-md active:scale-90"><i className="fa-solid fa-pen text-[10px]"></i></button>
                          <button onClick={() => handleDeleteRequest(card.id)} className="w-8 h-8 bg-white/10 hover:bg-red-500/20 text-white/60 hover:text-red-400 rounded-lg flex items-center justify-center transition-all border border-white/10 backdrop-blur-md active:scale-90"><i className="fa-solid fa-trash-can text-[10px]"></i></button>
                       </div>
                    </div>
                  </div>

                  <div className="relative z-10">
                    <p className="text-white/40 text-[7px] font-black uppercase tracking-[0.2em] mb-1">Limite Disponível Global</p>
                    <h3 className="text-2xl md:text-4xl font-black text-white tracking-tighter leading-none mb-2 heading-display group-hover:scale-105 transition-transform origin-left">{maskValue(availableLimit)}</h3>
                    <p className="font-mono text-white/20 text-[10px] md:text-sm tracking-[0.3em]">•••• •••• •••• {Math.floor(1000 + Math.random() * 8999)}</p>
                  </div>

                  <div className="relative z-10 space-y-2 md:space-y-3">
                    <div className="space-y-1 md:space-y-1.5">
                       <div className="flex justify-between text-[7px] md:text-[9px] font-black text-white/40 uppercase tracking-widest px-1">
                          <span>Uso do Limite: {usagePercent.toFixed(1)}%</span>
                          <span>Fatura {months[selectedMonth]}: {maskValue(billAmount)}</span>
                       </div>
                       <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden border border-white/5 p-0.5">
                          <div className={`h-full rounded-full transition-all duration-1000 ${usagePercent > 80 ? 'bg-red-500 shadow-[0_0_12px_rgba(239,68,68,0.6)]' : 'bg-white shadow-[0_0_12px_rgba(255,255,255,0.4)]'}`} style={{ width: `${Math.min(100, usagePercent)}%` }}></div>
                       </div>
                    </div>

                    <div className="flex justify-between items-end">
                      <div className="flex-1 min-w-0 mr-3">
                        <span className="text-white/30 text-[6px] font-black uppercase tracking-widest mb-0.5 block">Identificação do Cartão</span>
                        <p className="text-white text-[10px] md:text-base font-black uppercase tracking-widest truncate heading-display group-hover:text-blue-300 transition-colors">{card.name}</p>
                      </div>
                      <button onClick={() => handlePayBill(card, billAmount)} disabled={billAmount <= 0} className={`shrink-0 flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 rounded-xl font-black text-[8px] md:text-[10px] uppercase tracking-widest transition-all shadow-xl ${billAmount > 0 ? 'bg-white text-slate-900 hover:bg-slate-100 active:scale-95 shadow-white/10' : 'bg-white/10 border border-white/10 text-emerald-400 cursor-default opacity-50'}`}>
                        <i className={`fa-solid ${billAmount > 0 ? 'fa-wallet' : 'fa-check-circle'}`}></i>
                        {billAmount > 0 ? 'Pagar Fatura' : 'Fatura Paga'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <CardModal isOpen={isCardModalOpen} onClose={() => { setIsCardModalOpen(false); setEditingCard(null); }} onSave={(card) => { if (editingCard) onUpdateCard({ ...editingCard, ...card }); else onAddCard(card); }} initialData={editingCard || undefined} />
      <TransactionModal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setPayBillData(undefined); }} onAdd={onAddTransaction} cards={cards} transactions={transactions} initialData={payBillData} />
      
      <ConfirmDialog 
        isOpen={confirmDelete.isOpen} 
        onClose={() => setConfirmDelete({ isOpen: false, id: null, type: 'confirm' })} 
        onConfirm={() => confirmDelete.id && onDeleteCard(confirmDelete.id)} 
        title={confirmDelete.type === 'alert' ? "Ação Bloqueada" : "Remover Cartão?"} 
        message={confirmDelete.type === 'alert' 
          ? "Este cartão não pode ser excluído no momento pois existem faturas ou parcelas em aberto. Para prosseguir, quite todos os débitos registrados neste cartão." 
          : "Tem certeza que deseja remover este cartão?"} 
        confirmText={confirmDelete.type === 'alert' ? "Entendido" : "Confirmar Exclusão"} 
        type={confirmDelete.type === 'alert' ? 'warning' : 'danger'}
        isAlert={confirmDelete.type === 'alert'}
      />

      <ConfirmDialog 
        isOpen={confirmReminders.isOpen} 
        onClose={() => setConfirmReminders({ isOpen: false, card: null })} 
        onConfirm={() => confirmReminders.card && handleCreateYearlyReminders(confirmReminders.card)} 
        title="Gerar Lembretes Anuais?" 
        message={`Deseja agendar 12 lembretes de pagamento (um para cada mês do ano) para o cartão ${confirmReminders.card?.name}? Isso criará eventos automáticos na sua agenda para facilitar seu controle.`} 
        confirmText="Gerar Lembretes" 
        type="info"
      />

      {statementData.card && (
        <CardStatementModal 
          isOpen={statementData.isOpen}
          onClose={() => setStatementData(prev => ({ ...prev, isOpen: false }))}
          card={statementData.card}
          transactions={statementData.transactions}
          monthName={months[selectedMonth]}
          totalAmount={statementData.amount}
        />
      )}
    </div>
  );
};

export default Cards;
